import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

interface DatesRequestErrorLike {
  code?: string
  message?: string
  errors?: DatesRequestErrorLike | Array<DatesRequestErrorLike | string | null> | string | null
}

const messages = defineMessages({
  datesRequestAlreadyExists: {
    id: 'ZjQsgP0vSy9PHCbZRuMu',
    defaultMessage: 'A request for these dates already exists for this booking. Please check your order for the latest status.',
    description: 'Error message shown when a customer submits a duplicate dates request for the same booking in checkout or my escapes flows.',
  },
  datesRequestOutsidePolicy: {
    id: 'AMR-wdHC9gNXXcl1gAaG',
    defaultMessage: 'These dates are outside the self-service policy for this booking. Please contact our support team for help with your request.',
    description: 'Error message shown when a customer submits a dates request that can no longer be handled within the normal self-service date policy.',
  },
})

const fmt = (intl: IntlShape | undefined, descriptor: MessageDescriptor): string =>
  intl ? intl.formatMessage(descriptor) : descriptor.defaultMessage as string

const getNestedErrorCode = (
  error: DatesRequestErrorLike | undefined,
): string | undefined => {
  const nestedErrors = error?.errors

  if (!nestedErrors) {
    return undefined
  }

  if (Array.isArray(nestedErrors)) {
    const nestedErrorWithCode = nestedErrors.find(
      nestedError => typeof nestedError === 'object' && !!nestedError?.code,
    )

    return typeof nestedErrorWithCode === 'object' ? nestedErrorWithCode?.code : undefined
  }

  return typeof nestedErrors === 'object' ? nestedErrors.code : undefined
}

const getDatesRequestErrorCode = (
  error: DatesRequestErrorLike | undefined,
): string | undefined => error?.code ?? getNestedErrorCode(error)

export default function getDatesRequestErrorMessage(
  error: DatesRequestErrorLike | undefined,
  fallbackMessage: string,
  intl?: IntlShape,
): string {
  switch (getDatesRequestErrorCode(error)) {
    case 'dates_request_already_exists':
      return fmt(intl, messages.datesRequestAlreadyExists)
    case 'dates_request_outside_policy':
      return fmt(intl, messages.datesRequestOutsidePolicy)
    default:
      return fallbackMessage
  }
}
